import { Link, useLocation } from 'react-router-dom';
import { Bell, BookOpen, Search } from 'lucide-react';
import { useDashboardMentions } from '../api/dashboard.js';
import type { Tenant } from '../api/types.js';
import { initials } from '../lib/initials.js';

interface TopBarProps {
  onOpenPalette: () => void;
  onSignOut: () => void;
  callerEmail: string | null;
  callerDisplayName: string;
  tenants: Tenant[];
  annuaireHref: string | null;
  validatorOnly?: boolean;
}

// REQ-FE-034 area B — TopBar ported verbatim from the mockup source
// at `.claude/worktrees/feat+web-vite-react-scaffold/apps/web/src/components/Topbar.tsx`.
// Tailwind utility classes copied character-for-character. Adapted only:
// router (react-router-dom), data hooks (real `useDashboardMentions`),
// avatar popover (live keeps the FE-032 sign-out behaviour).
//
// Differences vs mockup:
// - Annuaire is a route link (existing live behaviour from REQ-FE-029e),
//   not a drawer-opener button.
// - Webhook drawer button dropped (live doesn't have that drawer yet).
// - Mentions bell with badge added (FE-029c-2 live carryover).
// - Avatar replaces the mockup's missing avatar — clicking it opens
//   the sign-out popover (FE-032 D20).
export function TopBar({
  onOpenPalette,
  onSignOut,
  callerEmail,
  callerDisplayName,
  tenants,
  annuaireHref,
  validatorOnly = false,
}: TopBarProps) {
  const { pathname } = useLocation();
  const breadcrumb = buildBreadcrumb(pathname, tenants);
  const mentionsQ = useDashboardMentions(20);
  const mentionsCount = mentionsQ.data?.length ?? 0;

  return (
    <header
      className="flex h-12 items-center justify-between px-6"
      style={{ borderBottom: '1px solid var(--gray-200)', background: 'var(--white)' }}
    >
      <Link to="/dashboard" className="flex items-center gap-1">
        <span
          className="text-base font-medium"
          style={{ fontFamily: 'var(--font-display)', letterSpacing: '-0.01em' }}
        >
          Altyor
        </span>
        <span className="text-coral text-base font-medium leading-none">.</span>
      </Link>

      <nav className="flex items-center gap-2 text-xs" style={{ color: 'var(--gray-600)' }}>
        {breadcrumb.map((part, i) => (
          <span key={i} className="flex items-center gap-2">
            {i > 0 && <span aria-hidden="true">/</span>}
            <span>{part}</span>
          </span>
        ))}
      </nav>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onOpenPalette}
          className="flex items-center gap-2 px-2 py-1 text-xs"
          style={{
            border: '1px solid var(--gray-200)',
            borderRadius: '2px',
            background: 'var(--white)',
          }}
          title="Recherche globale (⌘K)"
          data-testid="topbar-search"
        >
          <Search size={13} strokeWidth={1.5} style={{ color: 'var(--gray-500)' }} />
          <span style={{ color: 'var(--gray-500)' }}>Rechercher…</span>
          <span className="flex items-center gap-0.5">
            <kbd
              className="inline-flex h-4 min-w-[1rem] items-center justify-center px-1 text-[9px] font-semibold"
              style={{
                color: 'var(--ink)',
                background: 'var(--white)',
                border: '1px solid var(--gray-300)',
                borderRadius: '2px',
                boxShadow: '0 1px 0 var(--gray-200)',
                fontFamily: 'var(--font-body)',
              }}
            >
              ⌘
            </kbd>
            <kbd
              className="inline-flex h-4 min-w-[1rem] items-center justify-center px-1 text-[9px] font-semibold"
              style={{
                color: 'var(--ink)',
                background: 'var(--white)',
                border: '1px solid var(--gray-300)',
                borderRadius: '2px',
                boxShadow: '0 1px 0 var(--gray-200)',
                fontFamily: 'var(--font-body)',
              }}
            >
              K
            </kbd>
          </span>
        </button>

        {!validatorOnly && annuaireHref ? (
          <Link
            to={annuaireHref}
            className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium"
            style={{
              color: 'var(--gray-700)',
              border: '1px solid var(--gray-200)',
              borderRadius: '2px',
              background: 'var(--white)',
              textDecoration: 'none',
            }}
            aria-label="Annuaire fournisseurs"
            title="Annuaire SIREN / SIRET (⇧A)"
            data-testid="topbar-annuaire"
          >
            <BookOpen size={13} strokeWidth={1.5} />
            Annuaire
          </Link>
        ) : null}

        <button
          type="button"
          className="relative flex h-7 w-7 items-center justify-center"
          style={{
            color: 'var(--gray-600)',
            border: '1px solid var(--gray-200)',
            borderRadius: '2px',
            background: 'var(--white)',
          }}
          aria-label={mentionsCount > 0 ? `Mes mentions (${mentionsCount})` : 'Mes mentions'}
          data-testid="topbar-mentions"
        >
          <Bell size={14} strokeWidth={1.5} />
          {mentionsCount > 0 ? (
            <span
              className="absolute -top-1 -right-1 inline-flex min-w-[14px] items-center justify-center px-1 text-[9px] font-semibold"
              style={{
                background: 'var(--brand-coral)',
                color: 'var(--white)',
                borderRadius: '7px',
                height: '14px',
              }}
              data-testid="topbar-mentions-badge"
            >
              {mentionsCount > 99 ? '99+' : mentionsCount}
            </span>
          ) : null}
        </button>

        {/* Avatar with popover (FE-032 D20 — sign-out lives here, not
            in the sidebar). */}
        <details className="relative" data-testid="topbar-avatar-menu">
          <summary
            className="inline-flex h-7 w-7 cursor-pointer list-none items-center justify-center text-[10px] font-semibold"
            style={{
              background: 'var(--ink)',
              color: 'var(--white)',
              borderRadius: '999px',
            }}
            aria-label={callerEmail ?? 'Compte utilisateur'}
            data-testid="topbar-avatar"
          >
            {initials(callerDisplayName)}
          </summary>
          <div
            className="absolute right-0 z-20 mt-2 flex w-56 flex-col gap-1.5 p-2.5"
            style={{
              background: 'var(--white)',
              border: '1px solid var(--gray-200)',
              borderRadius: '2px',
              boxShadow: '0 8px 24px rgba(11, 11, 11, 0.16)',
            }}
          >
            <div
              className="text-[12px] font-medium break-all"
              style={{ color: 'var(--ink)' }}
              data-testid="topbar-avatar-email"
            >
              {callerEmail ?? '—'}
            </div>
            <button
              type="button"
              onClick={onSignOut}
              className="cursor-pointer pt-1.5 text-left text-[12px]"
              style={{
                background: 'transparent',
                border: 0,
                borderTop: '1px solid var(--gray-100)',
                color: 'var(--brand-coral)',
                fontFamily: 'inherit',
              }}
              data-testid="topbar-signout"
            >
              Se déconnecter
            </button>
          </div>
        </details>
      </div>
    </header>
  );
}

// REQ-FE-037 item 8 — map tenant UUID → slug for breadcrumb label.
// Routing still uses the UUID; the human-readable crumb shows the
// slugified legalName so the breadcrumb reads e.g.
// `Tenants / altyor-industrie-france / invoices`.
function buildBreadcrumb(pathname: string, tenants: readonly Tenant[]): string[] {
  if (pathname === '/' || pathname === '/dashboard') return ['Vue d’ensemble'];
  if (pathname === '/login') return ['Connexion'];
  if (pathname.startsWith('/admin/')) return ['Administration', pathname.split('/')[2] ?? ''];
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] === 't' && parts[1]) {
    const uuid = parts[1];
    const tenant = tenants.find((t) => t.tenantId === uuid);
    const label = tenant ? tenant.legalName.toLowerCase().replace(/\s+/g, '-') : uuid;
    const out = ['Tenants', label];
    if (parts[2]) out.push(parts[2]);
    return out;
  }
  return parts;
}
